import * as React from "react";
import classNames from "classnames";
import { $, merge } from "glamor";
import { Map } from "immutable";
import { theme } from "styles/theme";
import { CellMeasurerCache } from "react-virtualized/dist/commonjs/CellMeasurer";
import { isEmpty } from "lodash";
import { Search } from "components/icons/assortments/search";
import TextField from "material-ui/TextField";
import IconButton from "material-ui/IconButton";
import Cancel from "material-ui/svg-icons/navigation/cancel";
import { DATATEST } from "constants/integration";

interface IProps {
    disabled?: boolean;
    entities: any;
    ids: string[];
    isMobileOrPhablet: boolean;
    location: any;
    scope: any;
    sidePanelWidth: number;
    hideIcon?: boolean;
    router: any;
    selected?: boolean;
    catalog?: any;
    catalogs?: any;
}

interface IState {
    anchorEl: any;
    filters: Map<any, any>;
    open: boolean;
    resource: string;
    id: string | null;
    label: string | null;
    searchValue: string;
    mouseOver: boolean;
}

const TextFieldComponent: React.FunctionComponent<{
    searchValue?: string;
    label: string;
    onChange: (value: string) => void;
    onKeyPress?: (e: React.KeyboardEvent<{}>) => void;
    mouseOver: boolean;
}> = ({ label, onKeyPress, searchValue, onChange, mouseOver }) => {
    const [value, setValue] = React.useState<string>("");
    const [focus, setFocus] = React.useState<boolean>(false);
    const textFieldRef = React.useRef<TextField>();

    React.useEffect(() => {
        setValue(searchValue);
    }, [searchValue]);

    const handleChange = React.useCallback(
        (event) => {
            setValue(event.target.value);
            onChange(event.target.value);
        },
        [onChange]
    );

    const handleClear = React.useCallback(() => {
        handleChange({ target: { value: "" } });
        textFieldRef.current.focus();
    }, [handleChange]);

    const handleFocus = React.useCallback(() => {
        setFocus(true);
    }, []);

    const handleBlur = React.useCallback(() => {
        setFocus(false);
    }, []);

    return (
        <div>
            <TextField
                data-test={DATATEST.ASSORTMENTS.GLOBAL_SEARCH_INPUT}
                id="text-field-controlled"
                ref={textFieldRef}
                value={value || ""} // fixed error: A component is changing a controlled input of type text to be uncontrolled
                onChange={handleChange}
                onKeyPress={onKeyPress}
                style={{ height: "32px", width: "320px" }}
                inputStyle={{ padding: "0" }}
                className="searchInput"
                hintStyle={{
                    padding: "0x",
                    height: "32px",
                    lineHeight: "32px",
                    bottom: "0px",
                    width: "218px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    wordBreak: "break-all",
                    whiteSpace: "nowrap",
                }}
                underlineShow={false}
                hintText={label === "...loading" ? "" : `Search ${label}`}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
            <Cancel
                className={classNames("cancel", { cancelShow: value && value !== "" && (mouseOver || focus) })}
                onClick={handleClear}
            />
        </div>
    );
};

export class BrandPicker extends React.PureComponent<IProps, IState> {
    textFieldRef;
    cache = new CellMeasurerCache({
        defaultHeight: 48,
        fixedWidth: true,
        minHeight: 48,
    });

    styles = merge(
        {
            alignItems: "center",
            display: "flex",
            color: "#989898",
            minHeight: "17px",
            height: "100% !important",
            marginRight: "8px",
        },
        $(" .iconContainer", {
            alignItems: "center",
            background: theme.palette.darkGrey,
            borderRadius: "15%",
            display: "flex",
            height: "32px",
            justifyContent: "center",
            minWidth: "32px",
            width: "32px",
        }),
        $(" .brandName", {
            alignItems: "center",
            cursor: "pointer",
            display: "flex",
            fontSize: "12px",
            paddingRight: "12px",
            position: "relative",
            height: "32px",
            maxWidth: "240px",
            minWidth: "200px",
            textAlign: "right",
            whiteSpace: "nowrap",
        }),
        $(" .brandName svg", {
            fill: `${theme.palette.darkGrey} !important`,
        }),
        $(" span", {
            minWidth: "calc(100% - 24px)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        }),
        $(" .label", {
            width: "100%",
            color: theme.palette.darkGrey,
            paddingLeft: "16px",
            letterSpacing: "0.15px",
        }),
        $(" .searchInput", {
            flexGrow: "1",
            // height: "32px !important",
            borderBottom: `1px solid ${theme.palette.borderColor}`,
            fontSize: "14px !important",
            paddingRight: "60px",
            width: "100% !important",
            maxWidth: "320px",
            // lineHeight: "18px !important",
        }),
        $(" .searchInput > div:first-child", {
            width: "100% !important",
            paddingRight: "32px !important",
        }),
        $(" .searchHolder", {
            position: "relative",
        }),
        $(" .searchIconButton", {
            position: "absolute !important",
            right: 0,
            top: "-8px",
            zIndex: 2,
        }),
        $(" .searchIconButton svg", {
            stroke: "currentcolor",
            position: "relative",
            bottom: "0px !important",
            right: "0px !important",
            width: "18px !important",
            height: "18px !important",
            color: `${theme.palette.grey} !important`,
        }),
        $(" .searchIconButton.hasText svg", {
            color: `${theme.palette.accent1Color} !important`,
        }),
        $(" .searchIconButton:hover svg", {
            color: `${theme.palette.accent1Color} !important`,
        }),
        $(" .cancel", {
            position: "absolute",
            right: "40px",
            height: "17px !important",
            width: "17px !important",
            color: `${theme.palette.lightGrey} !important`,
            top: "7px",
            opacity: 0,
            zIndex: 10000,
        }),
        $(" .cancelShow", {
            opacity: 1,
        })
    );

    muiStyles = {
        list: {
            maxHeight: "300px",
            maxWidth: "224px",
        },
        mobileList: {
            maxHeight: "256px",
            maxWidth: "192px",
        },
        popover: {
            maxWidth: "256px",
            padding: "12px",
            overflowY: "auto",
        },
    };

    constructor(props) {
        super(props);
        this.state = {
            anchorEl: undefined,
            filters: Map(),
            open: false,
            resource: "all",
            id: null,
            label: "all",
            searchValue: "",
            mouseOver: false,
        };
    }

    UNSAFE_componentWillMount() {
        const { router } = this.props;
        let filters = router.location.query.filters || "{}";
        try {
            filters = JSON.parse(filters);
        } catch (e) {
            filters = {};
        }

        this.setSelectorValues();
        this.setState({ searchValue: filters.__search });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.pathname !== this.props.location.pathname || prevProps.catalog !== this.props.catalog) {
            this.setSelectorValues();
        }

        if (prevProps.location.search !== this.props.location.search) {
            this.handleFiltersChange();
        }

        if (prevProps.location.pathname !== this.props.location.pathname) {
            this.handleSwitchToBrandPage(prevProps);
        }
    }

    render() {
        const { selected } = this.props;
        const { label, searchValue, mouseOver } = this.state;

        return (
            <div className={classNames("sidePanelBrandPicker", { selected })} {...this.styles}>
                <div className="searchHolder" onMouseLeave={this.handleMouseLeave} onMouseEnter={this.handleMouseEnter}>
                    <TextFieldComponent
                        searchValue={searchValue}
                        onChange={this.handleChange}
                        onKeyPress={this.handleKeyPress}
                        label={label}
                        mouseOver={mouseOver}
                    />
                    <IconButton
                        data-test={DATATEST.ASSORTMENTS.GLOBAL_SEARCH_ENTER}
                        className={classNames("searchIconButton", { hasText: !!searchValue })}
                        onClick={this.handleSearch}
                    >
                        <Search />
                    </IconButton>
                </div>
            </div>
        );
    }

    private handleMouseLeave = () => {
        this.setState({ mouseOver: false });
    };

    private handleMouseEnter = () => {
        this.setState({ mouseOver: true });
    };

    private handleSwitchToBrandPage = (prevProps: any) => {
        const { location } = this.props;
        const isNowBrandRoute = location.pathname.match("/brand/") && !location.pathname.match("/catalog/");
        const wasLinesheetRoute =
            prevProps.location.pathname.match("/brand/") && prevProps.location.pathname.match("/catalog/");
        if (wasLinesheetRoute && isNowBrandRoute) {
            this.updatePageWithSearch(undefined);
        }
    };

    private setSelectorValues = () => {
        const { router, entities, catalogs, location } = this.props;
        const { params } = router;
        const brand = entities?.[params.brand];
        const catalogId = params.catalog;
        const catalog = catalogs ? catalogs[catalogId] : null;
        let resource = "all";
        let id = null;
        let label = "All";
        const isBrandRouteOnly = location.pathname.match("/brand/") && !location.pathname.match("/catalog/");
        const isLinesheetRoute = location.pathname.match("/brand/") && location.pathname.match("/catalog/");

        // prevent malicious copy/paste into brand id param
        if (brand) {
            if (isBrandRouteOnly) {
                resource = "brand";
                id = params.brand;
                label = entities[id].name;
            } else if (isLinesheetRoute) {
                resource = "linesheet";
                id = params.catalog;
                if (catalog) {
                    label = `${catalog.title}`;
                } else {
                    label = "...loading";
                }
            }

            this.setState({ resource, id, label });
        } else {
            this.setState({ resource, id, label: "all" });
        }
    };

    private handleChange = (searchValue: string) => {
        this.setState({ searchValue });
    };

    private handleFiltersChange = () => {
        const queryParams = this.props.location.query.filters || {};
        let filters: any = {};
        try {
            filters = JSON.parse(queryParams);
        } catch (e) {
            // intentionall blank
        }
        const searchValue = filters.__search || "";
        this.setState({ searchValue });
    };

    private handleSearch = () => {
        const { resource, id, searchValue } = this.state;

        if (resource === "linesheet" && id !== "all") {
            this.updatePageWithSearch(searchValue);
        } else if (resource === "brand" && !searchValue) {
            this.redirectToBrandPage(id);
        } else {
            this.redirectToSearchPage(searchValue);
        }
    };

    private handleKeyPress = (event) => {
        if (event.key === "Enter") {
            this.handleSearch();
        }
    };

    private updatePageWithSearch = (searchTerm: string) => {
        const { router } = this.props;
        const { location } = router;
        const { pathname, query } = location;
        let filters = {};
        try {
            filters = JSON.parse(query.filters);
        } catch (e) {
            // intentionally blank
        }

        const updatedQueryParams: any = {
            ...filters,
            __search: searchTerm,
        };

        if (!updatedQueryParams.__search || updatedQueryParams.__search === "") {
            delete updatedQueryParams.__search;
        }

        router.push({
            pathname: `${pathname}`,
            query: {
                gridParams: query.gridParams,
                filters: isEmpty(updatedQueryParams) ? undefined : JSON.stringify(updatedQueryParams),
                mode: query.mode,
            },
        });
    };

    private redirectToBrandPage = (brandId: string) => {
        const { router } = this.props;
        const { location } = router;
        const { query } = location;
        router.push({
            pathname: `/assortment/${router.params.id}/shop/brand/${brandId}`,
            query: {
                gridParams: query.gridParams,
                mode: query.mode,
            },
        });
    };

    private redirectToSearchPage = (searchTerm: string) => {
        const { router, location } = this.props;
        let filterParams: any = {};
        if (location.query.filters) {
            try {
                filterParams = JSON.parse(location.query.filters);
                delete filterParams.__search;
            } catch (e) {
                // intentionally blank
            }
        }

        let queryParams = {
            ...filterParams,
            __search: searchTerm,
        };

        // ensure we tack on scope brand if coming from a linesheet(s) page
        // but not do this if they selected "all" in dropdown
        // queryParams._brand_id = [scope.brand._id];
        if (location.pathname.match("/linesheet")) {
            // we have to remove FDM filters before they move onto search
            queryParams = {
                __search: searchTerm,
            };
        }

        const query: any = {
            filters: JSON.stringify(queryParams),
        };

        if (location.query.gridParams) {
            query.gridParams = location.query.gridParams;
        }

        if (location.query.mode) {
            query.mode = location.query.mode;
        }

        router.push({
            pathname: router.params.brand
                ? `/assortment/${router.params.id}/shop/brand/${router.params.brand}/search`
                : `/assortment/${router.params.id}/shop/search`,
            query,
        });
    };
}
